import { cloneDeep } from "lodash";
import { useRoute } from "vue-router";

import { useCmsStore } from "@/stores/cms.store";
import { useCurriculumsStore } from "@/stores/curriculums.store";
import { useFilesStore } from "@/stores/files.store";
import { useOrganizationStore } from "@/stores/organization.store";
import { useProductsStore } from "@/stores/products.store";
import { useProgrammesStore } from "@/stores/programmes.store";
import { useUsersStore } from "@/stores/users.store";

import { ProductSummaryItem } from "@/models/product.model";

import { getAllowedProducts } from "@/utils/filter-products-helpers";

export const useStoreManager = () => {
  const curriculumsStore = useCurriculumsStore();
  const orgStore = useOrganizationStore();
  const usersStore = useUsersStore();
  const programmesStore = useProgrammesStore();
  const productsStore = useProductsStore();
  const filesStore = useFilesStore();
  const cmsStore = useCmsStore();

  const route = useRoute();

  const populateProductDetails = async (): Promise<void> => {
    await productsStore.populateProductSummary();
    if (usersStore.currentUser && productsStore.productSummary) {
      const allowedProducts: ProductSummaryItem[] = getAllowedProducts(
        productsStore.productSummary,
        usersStore.currentUser.permissions
      );
      // Populate the newest and non-archived products first
      let productsSortedByStatus: ProductSummaryItem[] = cloneDeep(
        allowedProducts
      ).sort((a: ProductSummaryItem, b: ProductSummaryItem) => {
        const isAArchived = a.status === "Archive";
        const isBArchived = b.status === "Archive";

        if (isAArchived && !isBArchived) return 1; // a archive → later
        if (!isAArchived && isBArchived) return -1; // b archive → later

        // Both are archived or both are not archived → sort by descending id
        return b.id - a.id;
      });
      if (route.params.productId) {
        productsSortedByStatus = productsSortedByStatus.filter(
          (product: ProductSummaryItem) => {
            return product.id !== Number(route.params.productId);
          }
        );
        await productsStore.populateProductDetails(
          Number(route.params.productId)
        );
      }
      for (const product of productsSortedByStatus) {
        await productsStore.populateProductDetails(product.id);
      }
    }
  };

  const populateOrganizationData = async (): Promise<void> => {
    await Promise.all([
      orgStore.populateOrganization(),
      usersStore.populateUsers()
    ]);
  };
  const populateGeneralData = (): void => {
    populateProductDetails();
    programmesStore.populateProgrammes();
    curriculumsStore.populateCurriculums();
    filesStore.populateFiles();
    cmsStore.populateCms();
  };

  return {
    populateProductDetails,
    populateOrganizationData,
    populateGeneralData
  };
};
